import { IBreadcrumbItem, MessageBarType } from '@fluentui/react';
import { Loader, useHaicPageTitle, useToast } from '@h2oai/ui-kit';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { workspaceName } from '../../authz/gen/ai/h2o/workspace/v1/workspace_pb';
import { useUsers } from '../../authz/providers/UsersProvider';
import { FailedToLoadView } from '../../components/FailedToLoadView/FailedToLoadView';
import { HeaderPure } from '../../components/Header/Header';
import { NoItemView } from '../../components/NoItemView/NoItemView';
import { RowHeaderTitle } from '../../components/RowHeaderTitle/RowHeaderTitle';
import WidgetList from '../../components/WidgetList/WidgetList';
import { Job, Job_State } from '../../mlops/gen/ai/h2o/mlops/batch/v1/job_pb';
import { useMutationCancelJob, useMutationDeleteJob, useQueryJobs } from '../../mlops/mlops.batch.hooks';
import { useEnv } from '../../utils/hooks';
import { formatDefaultDateAndTime, formatDefaultDateAndTimeWithSeconds, formatError } from '../../utils/utils';
import { ContextMenuIconButton } from '../Orchestrator/Workflows';
import { calculateDuration, Tag } from '../Orchestrator/WorkflowTabExecutions';
import { getStateProps } from './BatchScoringDetail';
import { useConfirmDialog } from './ConfirmDialogProvider';
import { ROUTES } from './constants';
import { MLOPS_NO_ITEMS_BANNER_WIDTH } from './Deployments';
import { ListItemDescription } from './Models';
import NavigationWrapper from './NavigationWrapper';
import PageWrapper from './PageWrapper';
import { type GranularMLOpsPermissions, useGranularMLOpsPermissions } from './userActions';

export type JobItem = Job & {
  createdByName: string;
  duration?: string;
  onDeleteJob?: () => void;
  onEdit?: () => void;
  onCancel?: () => void;
  permissions: GranularMLOpsPermissions;
  actionsDisabled?: boolean;
};

const columns = [
  {
    key: 'title',
    name: 'Title',
    fieldName: 'name',
    minWidth: 340,
    maxWidth: 340,
    data: {
      headerFieldName: 'displayName',
      listCellProps: {
        onRenderHeader: ({ displayName, onEdit, permissions }: JobItem) =>
          RowHeaderTitle({ title: displayName, onClick: permissions.isAbleToViewBatchScoringJob ? onEdit : undefined }),
        onRenderText: ({ createdByName, createTime }: JobItem) => (
          <ListItemDescription createdBy={createdByName} createdAt={createTime || ``} />
        ),
        emptyMessage: 'No description',
      },
    },
  },
  {
    key: 'status',
    name: '',
    fieldName: 'state',
    minWidth: 90,
    maxWidth: 180,
    data: {
      listCellProps: {
        styles: {
          root: {
            justifyContent: 'center',
          },
        },
        onRenderText: ({ state }: JobItem) => {
          return state === Job_State.RUNNING ? (
            <Loader label="Running" />
          ) : (
            <Tag title={getStateProps(state).name} color={getStateProps(state).color} />
          );
        },
      },
    },
  },
  {
    key: 'duration',
    name: 'Duration',
    fieldName: 'duration',
    minWidth: 90,
    maxWidth: 120,
  },
  {
    key: 'startTime',
    name: 'Started at',
    fieldName: 'startTime',
    minWidth: 90,
    maxWidth: 180,
    data: {
      listCellProps: {
        onRenderText: ({ startTime }: JobItem) => {
          return startTime ? formatDefaultDateAndTime(startTime) : 'N/A';
        },
      },
    },
  },
  {
    key: 'endTime',
    name: 'Ended at',
    fieldName: 'endTime',
    minWidth: 90,
    maxWidth: 180,
    data: {
      listCellProps: {
        onRenderText: ({ endTime }: JobItem) => {
          return endTime ? formatDefaultDateAndTime(endTime) : 'N/A';
        },
      },
    },
  },
  {
    key: 'buttons',
    name: '',
    minWidth: 140,
    maxWidth: 200,
    data: {
      listCellProps: {
        emptyMessage: 'No description',
        onRenderText: ({ onDeleteJob, permissions, onEdit, onCancel, state }: JobItem) => (
          <ContextMenuIconButton
            items={[
              ...(permissions?.isAbleToViewBatchScoringJob
                ? [
                    {
                      key: 'view',
                      text: 'View',
                      onClick: onEdit,
                      iconProps: { iconName: 'RedEye', style: { color: 'var(--h2o-gray900)' } },
                    },
                  ]
                : []),
              {
                key: 'cancel',
                text: 'Cancel execution',
                onClick: onCancel,
                iconProps: { iconName: 'Cancel', style: { color: 'var(--h2o-yellow700)' } },
                style: {
                  color: 'var(--h2o-yellow700)',
                  display:
                    state !== Job_State.CANCELLED &&
                    state !== Job_State.CANCELLING &&
                    state !== Job_State.FAILED &&
                    state !== Job_State.FINISHED &&
                    state !== Job_State.SUBMISSION_FAILED &&
                    state !== Job_State.STATE_UNSPECIFIED &&
                    permissions?.isAbleToCancelBatchScoringJobs
                      ? undefined
                      : 'none',
                },
              },
              {
                key: 'delete',
                text: 'Delete',
                onClick: onDeleteJob,
                style: {
                  color: 'var(--h2o-red400)',
                  display:
                    (state === Job_State.CANCELLED ||
                      state === Job_State.FAILED ||
                      state === Job_State.FINISHED ||
                      state === Job_State.SUBMISSION_FAILED) &&
                    permissions?.isAbleToDeleteBatchScoringJobs
                      ? undefined
                      : 'none',
                },
                iconProps: { iconName: 'Delete', style: { color: 'var(--h2o-red400)' } },
              },
            ]}
          />
        ),
        styles: {
          root: {
            display: 'flex',
            flexGrow: 1,
            justifyContent: 'end',
          },
        },
      },
    },
  },
];

const BatchScoring = () => {
  const params = useParams<{ workspace_id: string }>();
  const workspaceFQDN = workspaceName.compile({ workspace: params.workspace_id });
  const permissions = useGranularMLOpsPermissions(workspaceFQDN);
  const [searchText, setSearchText] = React.useState<string>(``);
  const breadcrumbs: IBreadcrumbItem[] = [
    {
      key: `home`,
      text: `home`,
      onClick: () => history.push('/'),
    },
    {
      key: `mlops`,
      text: `MLOps`,
      onClick: () => history.push('/mlops'),
    },
    {
      key: 'jobs',
      text: 'Jobs',
      isCurrentItem: true,
    },
  ];
  const jobsQuery = useQueryJobs(
    params.workspace_id,
    { filter: searchText && `display_name = '*${searchText}*'`, orderBy: 'start_time desc', pageSize: 20 },
    {
      enabled: Boolean(permissions?.isAbleToListBatchScoringJobs),
      meta: {
        toastError: (err: unknown) => `Failed to fetch jobs: ${formatError(err)}`,
      },
      select: (data) => {
        const flattenedJobs = data.pages.flatMap((page) => page.jobs || []);
        return { pages: flattenedJobs, pageParams: data.pageParams };
      },
    }
  );
  const jobs = jobsQuery.data?.pages;
  const [lastRefreshed, setLastRefreshed] = React.useState<Date>();
  React.useEffect(() => {
    setLastRefreshed(new Date());
  }, [jobs]);

  const { addToast } = useToast();
  const cancelJobMutation = useMutationCancelJob({
    onError: (err) => {
      const message = `Failed to cancel job: ${formatError(err)}`;
      console.error(message);
      addToast({
        messageBarType: MessageBarType.error,
        message,
      });
    },
  });
  const deleteJobMutation = useMutationDeleteJob({
    onError: (err) => {
      const message = `Failed to delete job: ${formatError(err)}`;
      console.error(message);
      addToast({
        messageBarType: MessageBarType.error,
        message,
      });
    },
  });

  const loading = jobsQuery.isLoading || cancelJobMutation.isLoading || deleteJobMutation.isLoading;

  const history = useHistory(),
    env = useEnv(),
    { showDialog } = useConfirmDialog(),
    { usersNameMap } = useUsers(),
    onCreateButtonClick = () => history.push(`/mlops/${workspaceFQDN}${ROUTES.BATCH_SCORING}/start-new`),
    headerActionProps = permissions?.isAbleToCreateBatchScoringJobs
      ? {
          actionTitle: 'Start new job',
          onActionClick: onCreateButtonClick,
          actionIcon: 'Add',
        }
      : undefined;

  const jobItems = React.useMemo<JobItem[] | undefined>(() => {
    return jobs?.map(
      (item) =>
        ({
          ...item,
          createdByName: usersNameMap?.[item.creator || '']?.displayName || item.creator,
          duration: calculateDuration(item.startTime, item.endTime),
          onDeleteJob: () => {
            showDialog?.({
              title: 'Delete job?',
              description: 'Are you sure you want to delete this job? Once it is deleted it cannot be restored.',
              onConfirm: () => deleteJobMutation.mutate({ name: item.name || '' }),
            });
          },
          onEdit: () => history.push(`/mlops/${item.name}`),
          onCancel: () => cancelJobMutation.mutate({ name: item.name || '' }),
          permissions,
        } as JobItem)
    );
  }, [jobs]);

  useHaicPageTitle('Jobs | MLOps', env?.cloudInstanceName);

  return (
    <NavigationWrapper>
      <PageWrapper>
        {jobItems?.length || searchText || loading ? <HeaderPure items={breadcrumbs} pageTitle="Jobs" /> : null}
        <WidgetList
          columns={columns}
          items={jobItems}
          loading={loading}
          isLoadingSearch={Boolean(searchText)}
          isLoadingMore={jobsQuery.isFetchingNextPage}
          onLoadMore={jobsQuery.hasNextPage ? jobsQuery.fetchNextPage : undefined}
          lastRefreshed={formatDefaultDateAndTimeWithSeconds(lastRefreshed)}
          onRefresh={jobsQuery.refetch}
          actionProps={headerActionProps}
          searchProps={{
            placeholder: 'Search jobs',
            onSearchChange: (value) => {
              setSearchText(value);
            },
            searchText,
          }}
          NoItemsContent={NoItemView({
            title: 'No batch scoring jobs',
            description: `There are no jobs to display in this workspace. ${
              permissions?.isAbleToCreateBatchScoringJobs ? 'Start a new job.' : ''
            }`,
            actionTitle: permissions?.isAbleToCreateBatchScoringJobs ? 'Start new job' : undefined,
            onActionClick: onCreateButtonClick,
            actionIcon: 'Add',
            bannerWidth: MLOPS_NO_ITEMS_BANNER_WIDTH,
          })}
          ErrorContent={FailedToLoadView({
            title: 'Failed to load jobs',
            description: 'Please try again later. If the problem persists, contact our support.',
            actionTitle: 'Retry',
            onActionClick: jobsQuery.refetch,
            actionIcon: 'Refresh',
          })}
        />
      </PageWrapper>
    </NavigationWrapper>
  );
};

export default BatchScoring;
